// JournalReviewPage — 仕訳レビュー (複式簿記形式, #journal_review, 2026-07-02)
//
// freee の単式取引(deals)を複式簿記(借方/貸方)の仕訳単位で一覧・レビューするページ。
// デザインは参考HTML double_entry_preview_sorted_ledger.html の様式を再現
// (navyヘッダ / context-bar / ソートtoolbar / 開閉仕訳グループ / 借方=青・貸方=橙 ledger / 差額フッタ)。
//   - ソート: 発生日 / 支払期日 / 登録日(初回把握) / 更新日(変化検知) × 昇降 (未レビューが常に上)
//   - 仕訳修正指示: 各仕訳に指示を入力→D1保存+jobs配送 (このUI自体は freee へ書込まない = R2 非抵触。
//     反映はミニPC担当(AI)が指示を読んで freee を直接更新し、完了で「修正完了」になる)
//   - レビュー済み管理: POST /api/journal-review/marks — 行グレーアウト+進捗カウンタ+フィルタ
//   - 学習可否: 指示送信フォームの「今後のルールとして学習する」check (OFF = learn:false 一回限り)
//   - 類似仕訳検索: GET /api/deals/search (同一取引先・±90日・金額±20% 初期条件, 行下インライン展開)
//   - freee 取得は GET のみ (worker /api/journal-review/deals 経由・登録日/更新日は D1 差分検知)
//   - list mode は details[].description を返さないため、展開時に単体GETで摘要を lazy 補完
//   - 証憑添付 (2026-07-13): 各行 [証憑を添付] → 行下展開パネル。ファイルボックスNo指定
//     (POST /api/deals/:id/attach-receipt {receipt_number}) と ファイルアップロード
//     (POST /api/freee-public/receipts/upload → 返却 receipt_id で attach-receipt) の2経路。
//     成功で行に「証憑✓ n件」チップ。添付書込は worker 側 (UIクリック起点 = R2 準拠)。

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Button, InlineSpinner, MessageBlock } from '@freee_jp/vibes';
import { MdExpandMore, MdEditNote, MdWarningAmber, MdRefresh, MdInfoOutline, MdSearch, MdCheckCircle, MdSmartToy, MdAttachFile } from 'react-icons/md';
import {
  fetchAllJournalReviewDeals,
  fetchJournalReviewDealDetail,
  fetchJournalCorrections,
  createJournalCorrection,
  updateJournalCorrectionStatus,
  syncJournalReviewTimestamps,
  completeJournalReviewTimestampsSync,
  fetchJournalReviewMarks,
  setJournalReviewMark,
  attachReceiptToDeal,
  uploadFreeeReceipt,
  searchDeals,
  DealSearchRow,
  fetchAccountItemsMap,
  fetchSectionsMap,
  fetchTaxCodesMap,
  fetchItemsMap,
  fetchTagsMap,
  fetchAllFreeePartners,
  fetchWalletablesMap,
  JournalReviewDeal,
  JournalReviewDateField,
  JournalCorrection,
} from '../api/client';
import {
  dealToJournalGroup,
  JournalGroupData,
  LedgerLineData,
  EntryCellData,
  NameMaps,
} from '../utils/journalTransform';
import { correctionStatusToUnified } from '../lib/statusVocab';

// ---------------------------------------------------------------------------
// パレット (参考HTML :root の移植)
// ---------------------------------------------------------------------------
const C = {
  ground: '#edf1f5', ink: '#1d2a3a', sub: '#647386',
  line: '#d9e0e7', lineDark: '#bdc8d3', navy: '#203a57',
  blueTint: '#f0f6ff', blueLine: '#b9d2ed', orangeTint: '#fff7ee', orangeLine: '#ecd2ae',
  good: '#267a62', goodBg: '#edf8f4', warn: '#a46a12', warnBg: '#fdf3e3',
  danger: '#b3261e', dangerBg: '#fdecea',
};
const FONT = '"Hiragino Sans","Yu Gothic UI","Meiryo",-apple-system,"Segoe UI",sans-serif';
const NUM = '"SFMono-Regular",Consolas,"Liberation Mono",Menlo,monospace';

const S = {
  page: { background: C.ground, minHeight: '100vh', paddingBottom: '3rem', color: C.ink, fontFamily: FONT, fontSize: 14 } as React.CSSProperties,
  sheet: { position: 'relative', overflow: 'hidden', border: '1px solid #c8d1db', borderRadius: 10, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.08)', marginTop: 16, minWidth: 720 } as React.CSSProperties,
  // 処理中オーバーレイ: シート全体をグレーアウトし操作不可に (スクロールは可・クリック/入力は遮断)
  overlay: { position: 'absolute', inset: 0, zIndex: 60, background: 'rgba(247,249,251,.62)', backdropFilter: 'blur(1px)', cursor: 'wait' } as React.CSSProperties,
  overlayCard: { position: 'fixed', top: '42%', left: '50%', transform: 'translate(-50%,-50%)', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 22px', border: `1px solid ${C.lineDark}`, borderRadius: 8, background: '#fff', boxShadow: '0 9px 22px rgba(33,52,74,.18)', color: '#344b62', fontSize: 13, fontWeight: 800, maxWidth: 520 } as React.CSSProperties,
  freeeJump: { display: 'inline-block', marginTop: 3, padding: '1px 6px', border: '1px solid #d7e2ef', borderRadius: 3, background: '#fff', color: '#476b8e', fontSize: 9, fontWeight: 800, textDecoration: 'none', fontFamily: FONT, whiteSpace: 'nowrap' } as React.CSSProperties,
  top: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 58, padding: '8px 18px 8px 20px', color: '#fff', background: C.navy, flexWrap: 'wrap' } as React.CSSProperties,
  topTitleWrap: { display: 'flex', alignItems: 'baseline', gap: 14, minWidth: 0 } as React.CSSProperties,
  topTitle: { margin: 0, fontSize: 19, letterSpacing: '.025em', fontWeight: 700 } as React.CSSProperties,
  topCaption: { color: '#c9d6e5', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  headerChip: { padding: '6px 9px', border: '1px solid rgba(255,255,255,.28)', borderRadius: 4, color: '#e5eef8', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' } as React.CSSProperties,

  contextItem: { minHeight: 63, padding: '10px 13px', borderRight: `1px solid ${C.line}` } as React.CSSProperties,
  contextLabel: { display: 'block', marginBottom: 6, color: C.sub, fontSize: 10, letterSpacing: '.08em', fontWeight: 800 } as React.CSSProperties,
  contextValue: { display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, fontSize: 14, fontWeight: 700 } as React.CSSProperties,
  contextNote: { color: C.sub, fontSize: 11, fontWeight: 600, marginTop: 3 } as React.CSSProperties,

  toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', minHeight: 58, padding: '10px 18px 10px 20px', borderBottom: `1px solid ${C.line}`, background: '#fff' } as React.CSSProperties,
  toolbarLead: { display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0 } as React.CSSProperties,
  toolbarH2: { margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: '.025em', whiteSpace: 'nowrap' } as React.CSSProperties,
  toolbarP: { margin: 0, color: C.sub, fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  sortControl: { display: 'flex', alignItems: 'center', border: `1px solid ${C.lineDark}`, borderRadius: 5, overflow: 'hidden', background: '#fff' } as React.CSSProperties,
  sortLabel: { padding: '0 10px', color: C.sub, fontSize: 11, fontWeight: 800, letterSpacing: '.06em' } as React.CSSProperties,
  filterRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 18px 12px 20px', borderBottom: `1px solid ${C.line}`, background: '#fcfdfe' } as React.CSSProperties,
  filterInput: { height: 30, padding: '0 8px', border: `1px solid ${C.lineDark}`, borderRadius: 4, font: `600 12px ${FONT}`, color: '#3c4c5e', background: '#fff' } as React.CSSProperties,
  filterLabel: { color: C.sub, fontSize: 11, fontWeight: 800 } as React.CSSProperties,

  list: { padding: '14px 18px 18px', background: '#f7f9fb' } as React.CSSProperties,
  groupNo: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, borderRight: `1px solid ${C.line}`, color: '#3a5068', background: '#f3f6f9', fontFamily: NUM, fontWeight: 900, fontSize: 13 } as React.CSSProperties,
  groupNoSmall: { color: '#75869a', fontFamily: FONT, fontSize: 9, letterSpacing: '.1em', fontWeight: 800 } as React.CSSProperties,
  groupDates: { display: 'grid', gridTemplateColumns: '1fr 1fr', borderRight: `1px solid ${C.line}` } as React.CSSProperties,
  dateBlock: { display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 12px' } as React.CSSProperties,
  dateBlock2: { display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 12px', borderLeft: '1px solid #e9edf1' } as React.CSSProperties,
  dateLabel: { color: C.sub, fontSize: 10, letterSpacing: '.06em', fontWeight: 800 } as React.CSSProperties,
  dateValue: { marginTop: 5, color: '#2c3e52', fontFamily: NUM, fontSize: 12, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as React.CSSProperties,
  groupMain: { display: 'flex', minWidth: 0, flexDirection: 'column', justifyContent: 'center', padding: '10px 15px' } as React.CSSProperties,
  groupMainTop: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, flexWrap: 'wrap' } as React.CSSProperties,
  groupH3: { overflow: 'hidden', margin: 0, color: '#22364b', fontSize: 16, fontWeight: 800, letterSpacing: '.02em', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  journalId: { flex: '0 0 auto', padding: '3px 6px', border: '1px solid #d7e2ef', borderRadius: 3, color: '#476b8e', background: '#f3f8fd', fontFamily: NUM, fontSize: 10, fontWeight: 800 } as React.CSSProperties,
  groupP: { overflow: 'hidden', margin: '5px 0 0', color: C.sub, fontSize: 12, fontWeight: 600, textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  groupTotal: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 9, padding: '10px 12px', borderLeft: `1px solid ${C.line}` } as React.CSSProperties,
  groupTotalMeta: { display: 'flex', minWidth: 0, flexDirection: 'column', gap: 4 } as React.CSSProperties,
  groupTotalLabel: { color: C.sub, fontSize: 10, fontWeight: 800, letterSpacing: '.05em' } as React.CSSProperties,
  groupTotalValue: { color: '#203044', fontFamily: NUM, fontSize: 18, fontWeight: 900, letterSpacing: '-.035em', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as React.CSSProperties,
  chevron: { display: 'grid', placeItems: 'center', borderLeft: `1px solid ${C.line}`, color: '#698096' } as React.CSSProperties,

  detailArea: { padding: '13px 14px 14px', background: '#fff' } as React.CSSProperties,
  detailBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10, flexWrap: 'wrap' } as React.CSSProperties,
  detailBarLeft: { display: 'flex', alignItems: 'baseline', gap: 10 } as React.CSSProperties,
  detailH4: { margin: 0, color: '#344b62', fontSize: 13, fontWeight: 800 } as React.CSSProperties,
  detailP: { margin: 0, color: C.sub, fontSize: 11, fontWeight: 600 } as React.CSSProperties,

  ledger: { overflow: 'hidden', border: `1px solid ${C.lineDark}`, borderRadius: 5 } as React.CSSProperties,
  ledgerHead: { minHeight: 50, borderBottom: `1px solid ${C.lineDark}`, background: '#f4f7fa' } as React.CSSProperties,
  lineHead: { display: 'grid', placeItems: 'center', borderRight: `1px solid ${C.line}`, color: C.sub, fontSize: 10, letterSpacing: '.08em', fontWeight: 800 } as React.CSSProperties,
  sideTitle: { display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 } as React.CSSProperties,
  sideStrong: { fontSize: 15, letterSpacing: '.06em', fontWeight: 800 } as React.CSSProperties,
  sideSmallHead: { color: C.sub, fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  amountHead: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 13px', borderRight: `1px solid ${C.lineDark}`, color: C.sub, fontSize: 11, letterSpacing: '.06em', fontWeight: 800 } as React.CSSProperties,

  lineNo: { display: 'flex', justifyContent: 'center', paddingTop: 16, borderRight: `1px solid ${C.line}`, color: '#6e8095', fontFamily: NUM, fontSize: 12, fontWeight: 800 } as React.CSSProperties,
  entry: { position: 'relative', minWidth: 0, padding: '10px 13px 9px', borderRight: `1px solid ${C.line}` } as React.CSSProperties,
  entryBlank: { background: '#fbfcfd', borderRight: `1px solid ${C.line}`, color: '#b4c0cc', textAlign: 'center', paddingTop: 30, fontWeight: 700 } as React.CSSProperties,
  metaRow: { display: 'flex', overflow: 'hidden', marginTop: 7, padding: '6px 0 5px', borderTop: '1px solid #edf1f4', borderBottom: '1px solid #edf1f4', color: '#5e7083', whiteSpace: 'nowrap', fontSize: 11, lineHeight: '15px' } as React.CSSProperties,
  entryNote: { overflow: 'hidden', marginTop: 6, color: C.sub, fontSize: 11, fontWeight: 600, textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as React.CSSProperties,
  amountCell: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 6, padding: '8px 13px', borderRight: `1px solid ${C.lineDark}`, background: 'rgba(249,251,253,.72)' } as React.CSSProperties,
  amountBlank: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 13px', borderRight: `1px solid ${C.lineDark}`, background: 'rgba(249,251,253,.72)', color: '#c2cbd4', font: `700 16px ${NUM}` } as React.CSSProperties,
  amount: { color: '#223244', fontFamily: NUM, fontSize: 17, fontWeight: 800, letterSpacing: '-.025em', fontVariantNumeric: 'tabular-nums' } as React.CSSProperties,
  taxCode: { color: '#6c7d8e', fontSize: 10, fontWeight: 800, letterSpacing: '.02em' } as React.CSSProperties,

  groupFoot: { display: 'grid', gridTemplateColumns: '1fr 280px', border: `1px solid ${C.lineDark}`, borderTop: 0, borderRadius: '0 0 5px 5px', background: '#fbfcfd' } as React.CSSProperties,
  groupNote: { padding: '10px 13px', color: C.sub, fontSize: 11, fontWeight: 600, lineHeight: 1.55 } as React.CSSProperties,

  correctionPanel: { marginTop: 12, border: `1px solid ${C.lineDark}`, borderRadius: 5, background: '#f8fafc', overflow: 'hidden' } as React.CSSProperties,
  correctionHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '9px 13px', borderBottom: `1px solid ${C.line}`, fontSize: 12, fontWeight: 800, color: '#344b62', background: '#f4f7fa' } as React.CSSProperties,
  correctionForm: { display: 'flex', gap: 8, alignItems: 'flex-end', padding: '10px 13px', flexWrap: 'wrap' } as React.CSSProperties,
  correctionTextarea: { flex: 1, minWidth: 260, minHeight: 56, padding: 8, border: `1px solid ${C.lineDark}`, borderRadius: 4, font: `400 12.5px ${FONT}`, resize: 'vertical', boxSizing: 'border-box' } as React.CSSProperties,
  openBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: C.warnBg, border: '1px solid #e3c68a', color: C.warn, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  settledBadge: (ok: boolean) => ({ display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap', color: ok ? C.good : '#5e7083', background: ok ? C.goodBg : '#f1f4f7', border: `1px solid ${ok ? '#9fdbc7' : '#ccd6df'}` } as React.CSSProperties),
  readonlyPill: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', border: '1px solid rgba(255,255,255,.35)', borderRadius: 13, background: 'rgba(255,255,255,.12)', color: '#e5eef8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  toolbarHint: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', margin: 0, paddingTop: 6, borderTop: `1px dashed ${C.line}`, color: C.sub, fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  filterNote: { display: 'inline-flex', alignItems: 'center', gap: 5, color: C.sub, fontSize: 11, fontWeight: 600 } as React.CSSProperties,
  // レビュー済みバッジ (緑・✓)
  reviewedBadge: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: C.goodBg, border: '1px solid #9fdbc7', color: C.good, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap' } as React.CSSProperties,
  // 修正指示 状態チップ (クリックで指示内容と経過を展開)
  corrChipWait: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: C.warnBg, border: '1px solid #e3c68a', color: C.warn, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap', cursor: 'pointer' } as React.CSSProperties,
  corrChipDone: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: C.goodBg, border: '1px solid #9fdbc7', color: C.good, fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap', cursor: 'pointer' } as React.CSSProperties,
  // 類似仕訳検索 インラインパネル
  simPanel: { borderTop: `1px solid ${C.line}`, background: '#f8fafc' } as React.CSSProperties,
  simHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 13px', borderBottom: `1px solid ${C.line}`, fontSize: 12, fontWeight: 800, color: '#344b62', background: '#f4f7fa', flexWrap: 'wrap' } as React.CSSProperties,
  simConds: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 13px', borderBottom: `1px solid ${C.line}`, flexWrap: 'wrap', fontSize: 11, fontWeight: 700, color: '#3c4c5e' } as React.CSSProperties,
  simNumInput: { width: 58, height: 26, padding: '0 6px', border: `1px solid ${C.lineDark}`, borderRadius: 4, font: `700 12px ${NUM}`, color: '#3c4c5e', background: '#fff' } as React.CSSProperties,
  simTableWrap: { overflowX: 'auto', padding: '8px 13px 12px' } as React.CSSProperties,
  simTable: { width: '100%', borderCollapse: 'collapse', fontSize: 12, background: '#fff', border: `1px solid ${C.line}` } as React.CSSProperties,
  simTh: { padding: '6px 9px', borderBottom: `1px solid ${C.lineDark}`, background: '#f4f7fa', color: C.sub, fontSize: 10, fontWeight: 800, letterSpacing: '.05em', textAlign: 'left', whiteSpace: 'nowrap' } as React.CSSProperties,
  simTd: { padding: '6px 9px', borderBottom: `1px solid ${C.line}`, verticalAlign: 'top' } as React.CSSProperties,
  // 学習チェック
  learnLabel: { display: 'inline-flex', alignItems: 'center', gap: 5, color: '#3c4c5e', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' } as React.CSSProperties,
  learnOffNote: { color: C.warn, fontSize: 10.5, fontWeight: 800 } as React.CSSProperties,
};

// 関数style (S から分離 — BudgetActualPage CHIP 慣行)
const sortKeyBtnSt = (active: boolean): React.CSSProperties => ({
  height: 32, padding: '0 10px', border: 0, borderLeft: `1px solid ${C.line}`, cursor: 'pointer',
  font: `700 12px ${FONT}`,
  color: active ? '#234e79' : '#53657a', background: active ? C.blueTint : '#fff',
  boxShadow: active ? 'inset 0 -2px 0 #467da9' : undefined,
});
const sortOrderBtnSt: React.CSSProperties = {
  height: 32, minWidth: 84, padding: '0 10px', border: 0, borderLeft: `1px solid ${C.line}`,
  background: '#fff', color: '#53657a', font: `700 12px ${FONT}`, cursor: 'pointer',
};
const viewBtnSt: React.CSSProperties = {
  height: 32, padding: '0 11px', border: `1px solid ${C.lineDark}`, borderRadius: 4,
  background: '#fff', color: '#53657a', font: `700 12px ${FONT}`, cursor: 'pointer',
};
// reviewed = レビュー済み → 行全体グレーアウト (opacity + グレー背景)
const groupSt = (hasOpen: boolean, reviewed: boolean): React.CSSProperties => ({
  marginBottom: 12, overflow: 'hidden', borderRadius: 7,
  background: reviewed ? '#edf0f3' : '#fff',
  border: `1px solid ${hasOpen ? '#e3c68a' : C.lineDark}`,
  opacity: reviewed ? 0.6 : 1,
});
const summarySt = (open: boolean, narrow: boolean, reviewed: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: narrow ? '64px 220px minmax(0,1fr) 200px 28px' : '78px 250px minmax(0,1fr) 230px 28px',
  minHeight: 76, alignItems: 'stretch', cursor: 'pointer',
  background: reviewed ? 'transparent' : (open ? '#fbfcfd' : '#fff'),
  borderBottom: open ? `1px solid ${C.lineDark}` : 'none',
});
const ledgerGridSt = (narrow: boolean): React.CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: narrow
    ? '38px minmax(0,1fr) 112px 26px minmax(0,1fr) 112px'
    : '44px minmax(0,1fr) 128px 30px minmax(0,1fr) 128px',
});
const sideHeadSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 0, padding: '0 13px', gap: 8,
  borderRight: `1px solid ${side === 'debit' ? C.blueLine : C.orangeLine}`,
  background: side === 'debit' ? C.blueTint : C.orangeTint,
});
const sideStampSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  display: 'inline-grid', placeItems: 'center', width: 36, height: 23, borderRadius: 3,
  border: '1px solid currentColor', fontSize: 10, letterSpacing: '.08em', fontWeight: 900,
  color: side === 'debit' ? '#33699e' : '#a46922', flex: '0 0 auto',
});
const accountSt = (side: 'debit' | 'credit'): React.CSSProperties => ({
  overflow: 'hidden', fontSize: 15, fontWeight: 800, letterSpacing: '.02em',
  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  color: side === 'debit' ? '#284f75' : '#7e5320',
});
const ledgerRowSt = (even: boolean): React.CSSProperties => ({
  minHeight: 91, borderBottom: `1px solid ${C.line}`, background: even ? '#fcfdfe' : '#fff',
});
const groupBalanceSt = (ok: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 13px',
  borderLeft: `1px solid ${C.line}`, background: ok ? '#f1f8f5' : C.dangerBg,
});
// open指示 = 送信済で担当(Claude)の作業待ち = 「処理中(指示対応中)」青 (統一語彙 — あなたの操作は不要)
const correctionItemSt = (open: boolean): React.CSSProperties => ({
  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 13px',
  borderBottom: `1px solid ${C.line}`, fontSize: 12,
  background: open ? C.blueTint : '#fff', opacity: open ? 1 : 0.72,
});
const correctionStatusSt = (open: boolean): React.CSSProperties => ({
  flex: '0 0 auto', padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 800,
  color: open ? '#33699e' : C.good, background: open ? '#fff' : C.goodBg,
  border: `1px solid ${open ? C.blueLine : '#9fdbc7'}`, whiteSpace: 'nowrap',
});

// 擬似要素・hover・レスポンシブ細部のみ最小CSS
const PAGE_CSS = `
.jr-bridge { position: relative; border-right: 1px solid ${C.lineDark}; background: #f5f7fa; }
.jr-bridge::after { content: ""; position: absolute; top: 18px; bottom: 18px; left: 50%; border-left: 1px solid #dce4eb; }
.jr-bridge-head { position: relative; border-right: 1px solid ${C.lineDark}; background: #eef2f6; }
.jr-bridge-head::before { content: "\\21C4"; position: absolute; inset: 0; display: grid; place-items: center; color: #8092a5; font-weight: 800; }
.jr-detail-status { display: flex; gap: 8px; align-items: center; color: ${C.good}; font-size: 11px; font-weight: 800; }
.jr-detail-status::before { content: "\\2713"; display: grid; place-items: center; width: 17px; height: 17px; border: 1px solid #9fdbc7; border-radius: 50%; background: ${C.goodBg}; }
.jr-detail-status.ng { color: ${C.danger}; }
.jr-detail-status.ng::before { content: "\\0021"; border-color: #e6b0ac; background: ${C.dangerBg}; }
.jr-summary:hover { background: #f8fafc !important; }
.jr-hoverbtn:hover { background: #f5f8fb !important; }
.jr-meta-piece { display: inline-flex; min-width: 0; max-width: 42%; padding-right: 8px; margin-right: 8px; border-right: 1px solid #e1e7ec; overflow: hidden; text-overflow: ellipsis; }
.jr-meta-piece:last-child { margin-right: 0; border-right: 0; }
.jr-meta-key { flex: 0 0 auto; margin-right: 5px; color: #8998a7; font-size: 10px; font-weight: 800; letter-spacing: .04em; }
.jr-meta-value { overflow: hidden; font-weight: 700; text-overflow: ellipsis; }
.jr-chevron svg { transition: transform .16s ease; }
.jr-chevron.open svg { transform: rotate(180deg); }
@media (max-width: 1180px) {
  .jr-side-sub { display: none; }
  .jr-meta-piece { max-width: 48%; }
  .jr-context-last { display: none !important; }
}
`;

// ---------------------------------------------------------------------------

type SortKey = 'issue' | 'due' | 'created' | 'updated';
const SORT_LABELS: Record<SortKey, string> = {
  issue: '発生日', due: '支払期日', created: '登録日', updated: '更新日',
};
const SORT_TITLES: Partial<Record<SortKey, string>> = {
  created: '登録日 = freee 仕訳帳の作成日時(真値・分単位)。未同期の取引のみ FREEEMAN 初回把握時刻で近似',
  updated: '更新日 = freee 仕訳帳の更新日時(真値・分単位)。未同期の取引のみ FREEEMAN 変化検知時刻で近似',
};
const keyField = (k: SortKey): keyof JournalGroupData =>
  k === 'issue' ? 'issueDate' : k === 'due' ? 'dueDate' : k === 'created' ? 'createdAt' : 'updatedAt';

const fmtYen = (n: number) => `¥${n.toLocaleString('ja-JP')}`;
const fmtDate = (s: string | null | undefined) => (s ? s.replace(/-/g, '/') : '—');
const fmtStamp = (s: string | null | undefined) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s.slice(0, 10).replace(/-/g, '/');
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const useNarrow = (bp = 1180) => {
  const [n, setN] = useState(() => typeof window !== 'undefined' && window.innerWidth <= bp);
  useEffect(() => {
    const m = window.matchMedia(`(max-width:${bp}px)`);
    const h = (e: MediaQueryListEvent) => setN(e.matches);
    m.addEventListener('change', h);
    return () => m.removeEventListener('change', h);
  }, [bp]);
  return n;
};

const monthRange = (d: Date): { from: string; to: string } => {
  const y = d.getFullYear(); const m = d.getMonth();
  const p = (x: number) => String(x).padStart(2, '0');
  const last = new Date(y, m + 1, 0).getDate();
  return { from: `${y}-${p(m + 1)}-01`, to: `${y}-${p(m + 1)}-${p(last)}` };
};

const isOpenCorrection = (c: JournalCorrection) => c.status === 'open' || c.status === 'in_progress';

const chunk = <T,>(arr: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

// corrections を deal_ids 指定で取得 (chunk=100)。deal_ids 未対応 worker への防御:
// 失敗時は全件取得して client 側で絞る。
async function loadCorrectionsFor(ids: number[]): Promise<JournalCorrection[]> {
  if (!ids.length) return [];
  try {
    const parts = await Promise.all(chunk(ids, 100).map((c) => fetchJournalCorrections({ dealIds: c })));
    return parts.flatMap((p) => p.corrections);
  } catch {
    const r = await fetchJournalCorrections({});
    const set = new Set(ids);
    return r.corrections.filter((c) => set.has(c.deal_id));
  }
}

// marks を deal_ids 指定で取得。endpoint 未配備 (並行実装中) でも空 Map でページは機能継続。
async function loadMarksFor(ids: number[]): Promise<Map<number, boolean>> {
  const m = new Map<number, boolean>();
  if (!ids.length) return m;
  try {
    const parts = await Promise.all(chunk(ids, 100).map((c) => fetchJournalReviewMarks({ dealIds: c })));
    for (const p of parts) for (const mk of p) m.set(mk.deal_id, mk.reviewed);
    return m;
  } catch {
    try {
      const all = await fetchJournalReviewMarks({});
      const set = new Set(ids);
      for (const mk of all) if (set.has(mk.deal_id)) m.set(mk.deal_id, mk.reviewed);
    } catch { /* marks 未配備 — 全件未レビュー表示で継続 */ }
    return m;
  }
}

const addDaysStr = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setDate(d.getDate() + days);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// 類似検索の初期条件 (userの指定: 同一取引先・発生日±90日・金額±20%)
const SIM_DEFAULT_DAYS = 90;
const SIM_DEFAULT_PCT = 20;

// ---------------------------------------------------------------------------
// 明細セル
// ---------------------------------------------------------------------------
const EntryCell: React.FC<{ cell: EntryCellData | null; side: 'debit' | 'credit' }> = ({ cell, side }) => {
  if (!cell) return <div style={S.entryBlank}>—</div>;
  const metas: Array<[string, string]> = [];
  if (cell.partnerName) metas.push(['取引先', cell.partnerName]);
  if (cell.sectionName) metas.push(['部門', cell.sectionName]);
  if (cell.tagNames && cell.tagNames.length) metas.push(['タグ', cell.tagNames.join('・')]);
  if (cell.kind === 'payment' && cell.paymentDate) metas.push(['決済日', fmtDate(cell.paymentDate)]);
  const noteParts: string[] = [];
  if (cell.itemName) noteParts.push(`品目 ${cell.itemName}`);
  if (cell.description) noteParts.push(`摘要: ${cell.description}`);
  return (
    <div style={S.entry}>
      <div style={accountSt(side)}>{cell.accountName}</div>
      {metas.length > 0 && (
        <div style={S.metaRow}>
          {metas.map(([k, v]) => (
            <span key={k} className="jr-meta-piece">
              <span className="jr-meta-key">{k}</span>
              <span className="jr-meta-value">{v}</span>
            </span>
          ))}
        </div>
      )}
      {noteParts.length > 0 && <div style={S.entryNote}>{noteParts.join('　/　')}</div>}
    </div>
  );
};

const AmountCell: React.FC<{ cell: EntryCellData | null }> = ({ cell }) => {
  if (!cell) return <div style={S.amountBlank}>—</div>;
  return (
    <div style={S.amountCell}>
      <div style={S.amount}>{cell.amount.toLocaleString('ja-JP')}</div>
      <div style={S.taxCode}>{cell.kind === 'detail' ? (cell.taxLabel || '') : cell.kind === 'payment' ? '決済' : '未決済'}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// メインページ
// ---------------------------------------------------------------------------
export const JournalReviewPage: React.FC = () => {
  const narrow = useNarrow();
  const [maps, setMaps] = useState<NameMaps | null>(null);
  const [mapsError, setMapsError] = useState<string | null>(null);

  const initial = monthRange(new Date());
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [dateField, setDateField] = useState<JournalReviewDateField>('issue');

  const rawDealsRef = useRef<Map<number, JournalReviewDeal>>(new Map());
  const [journals, setJournals] = useState<JournalGroupData[]>([]);
  const [corrections, setCorrections] = useState<Map<number, JournalCorrection[]>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedRange, setLoadedRange] = useState<string>('');

  const [sortKey, setSortKey] = useState<SortKey>('issue');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [query, setQuery] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  // 修正指示フィルタ: '' = 全て / 'open' = 未解決あり / 'resolved' = 解決済(未解決なし)
  const [corrFilter, setCorrFilter] = useState<'' | 'open' | 'resolved'>('');
  // レビュー済みフィルタ: 'all' = 全て / 'unreviewed' = 未レビューのみ / 'reviewed' = レビュー済みのみ
  const [reviewFilter, setReviewFilter] = useState<'all' | 'unreviewed' | 'reviewed'>('all');

  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [sending, setSending] = useState<Record<number, boolean>>({});
  const [sendErr, setSendErr] = useState<Record<number, string | null>>({});
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // レビュー済みマーク (deal_id → reviewed)
  const [marks, setMarks] = useState<Map<number, boolean>>(new Map());
  const [marking, setMarking] = useState<Record<number, boolean>>({});
  const [markErr, setMarkErr] = useState<Record<number, string | null>>({});

  // 学習可否 (deal_id → false のみ「学習しない」。省略 = 既定ON)
  const [learnFlags, setLearnFlags] = useState<Record<number, boolean>>({});

  // 類似仕訳検索 (行下インライン展開)
  const [simOpen, setSimOpen] = useState<Set<number>>(new Set());
  const [simDays, setSimDays] = useState<Record<number, number>>({});
  const [simPct, setSimPct] = useState<Record<number, number>>({});
  const [simSamePartner, setSimSamePartner] = useState<Record<number, boolean>>({});
  const [simResults, setSimResults] = useState<Record<number, DealSearchRow[] | null>>({});
  const [simLoading, setSimLoading] = useState<Record<number, boolean>>({});
  const [simErr, setSimErr] = useState<Record<number, string | null>>({});

  // 証憑添付 (行下インライン展開パネル)
  const [attachOpen, setAttachOpen] = useState<Set<number>>(new Set());
  const [attachBusy, setAttachBusy] = useState<Record<number, boolean>>({});
  const [attachErr, setAttachErr] = useState<Record<number, string | null>>({});
  const [attachMsg, setAttachMsg] = useState<Record<number, string | null>>({});
  const [fileboxNoDrafts, setFileboxNoDrafts] = useState<Record<number, string>>({});
  // deal_id → 添付済み証憑数 (list/detail 応答の receipts があれば初期値、添付成功で更新)
  const [receiptCounts, setReceiptCounts] = useState<Record<number, number>>({});

  // マスタ名称 (module cache 済 = 初回のみ実 fetch)
  useEffect(() => {
    (async () => {
      try {
        const [accounts, sections, taxCodes, items, tags, partnersArr, wallets] = await Promise.all([
          fetchAccountItemsMap(), fetchSectionsMap(), fetchTaxCodesMap(),
          fetchItemsMap(), fetchTagsMap(), fetchAllFreeePartners(), fetchWalletablesMap(),
        ]);
        const partners = new Map<number, string>();
        for (const p of partnersArr) partners.set(Number(p.id), p.name);
        setMaps({ accounts, sections, taxCodes, items, tags, partners, wallets });
      } catch (e: unknown) {
        setMapsError(`マスタ取得失敗: ${e instanceof Error ? e.message : String(e)}`);
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!maps) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateFrom) || !/^\d{4}-\d{2}-\d{2}$/.test(dateTo)) {
      setError('期間は YYYY-MM-DD 形式で指定してください'); return;
    }
    setLoading(true); setError(null);
    try {
      const deals = await fetchAllJournalReviewDeals({ start: dateFrom, end: dateTo, dateField });
      rawDealsRef.current = new Map(deals.map((d) => [d.id, d]));
      setJournals(deals.map((d) => dealToJournalGroup(d, maps)));
      // 証憑数の初期値 (list 応答が receipts を返す場合のみ。未対応 worker は添付成功時に加算)
      const rc: Record<number, number> = {};
      for (const d of deals) if (Array.isArray(d.receipts)) rc[d.id] = d.receipts.length;
      setReceiptCounts(rc);
      // 修正指示 + レビュー済みマーク: 表示中 deal_ids に絞って取得 (応答形/未配備は防御的)
      const ids = deals.map((d) => d.id);
      const [corrArr, markMap] = await Promise.all([loadCorrectionsFor(ids), loadMarksFor(ids)]);
      const cm = new Map<number, JournalCorrection[]>();
      for (const c of corrArr) {
        const arr = cm.get(c.deal_id) || [];
        arr.push(c); cm.set(c.deal_id, arr);
      }
      for (const arr of Array.from(cm.values())) arr.sort((a, b) => a.id - b.id);
      setCorrections(cm);
      setMarks(markMap);
      const dfLabel = dateField === 'issue' ? '発生日' : dateField === 'due' ? '支払期日'
        : dateField === 'created' ? '登録日(初回把握)' : '更新日(変化検知)';
      setLoadedRange(`${fmtDate(dateFrom)} ～ ${fmtDate(dateTo)} (${dfLabel}基準)`);
      setExpanded(new Set());
    } catch (e: unknown) {
      setError(`取得失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setLoading(false);
    }
  }, [maps, dateFrom, dateTo, dateField]);

  // マスタ到着後に初回自動ロード
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (maps && !loadedOnce.current) { loadedOnce.current = true; load(); }
  }, [maps, load]);

  // 摘要 lazy fetch (list mode は description 欠落)
  const ensureDetail = useCallback((id: number) => {
    const g = journals.find((j) => j.id === id);
    if (g && !g.detailFetched && maps) {
      fetchJournalReviewDealDetail(id).then((full) => {
        if (!full) return;
        const raw = rawDealsRef.current.get(id);
        // first_seen/last_seen は list 応答が正 (単体GETには無い)
        full.first_seen_at = raw?.first_seen_at ?? null;
        full.last_seen_at = raw?.last_seen_at ?? null;
        rawDealsRef.current.set(id, full);
        // 単体GET (freee-proxy) は receipts を返す → 証憑数を真値へ更新
        if (Array.isArray(full.receipts)) {
          const n = full.receipts.length;
          setReceiptCounts((rc) => ({ ...rc, [id]: n }));
        }
        setJournals((js) => js.map((j) => (j.id === id ? dealToJournalGroup(full, maps, { detailFetched: true }) : j)));
      }).catch(() => { /* 摘要なしで表示継続 */ });
    }
  }, [journals, maps]);

  const toggleExpand = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      return next;
    });
    ensureDetail(id);
  }, [ensureDetail]);

  // 状態チップ/引用ボタンから: 閉じているときだけ開く (指示内容と経過を見せる)
  const ensureExpanded = useCallback((id: number) => {
    setExpanded((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    ensureDetail(id);
  }, [ensureDetail]);

  const expandAll = useCallback(() => setExpanded(new Set(journals.map((j) => j.id))), [journals]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  // freee 真値タイムスタンプ同期 (仕訳帳 generic_v2・直近12ヶ月・freee へは GET のみ)。202=job継続中 → complete で回収。
  const runFreeeSync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true); setSyncMsg(null);
    try {
      const today = new Date();
      const from = new Date(today.getTime() - 365 * 24 * 3600 * 1000);
      const p = (x: number) => String(x).padStart(2, '0');
      const d2s = (d: Date) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
      const startDate = d2s(from), endDate = d2s(today);
      let r = await syncJournalReviewTimestamps(startDate, endDate);
      let tries = 0;
      while (r.status === 202 && r.body.report_id && tries < 8) {
        setSyncMsg(`freee 仕訳帳を生成中… (${(tries + 1) * 15}秒)`);
        await new Promise((res) => setTimeout(res, 15000));
        r = await completeJournalReviewTimestampsSync(r.body.report_id, startDate, endDate);
        tries += 1;
      }
      if (r.status === 200 && r.body.ok) {
        setSyncMsg(`同期完了: ${(r.body.deals_upserted || 0).toLocaleString('ja-JP')}件の取引に freee 真値を反映`);
        await load(); // 再取得して真値を表示に反映
      } else {
        setSyncMsg(`同期未完了 (HTTP ${r.status})。時間をおいて再実行してください`);
      }
    } catch (e: unknown) {
      setSyncMsg(`同期失敗: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSyncing(false);
    }
  }, [syncing, load]);

  // 修正指示 送信 (learn=false は「一回限りの適用・学習しない」)
  const submitCorrection = useCallback(async (dealId: number) => {
    const text = (drafts[dealId] || '').trim();
    if (!text || sending[dealId]) return;
    const learn = learnFlags[dealId] !== false;
    setSending((s) => ({ ...s, [dealId]: true }));
    setSendErr((s) => ({ ...s, [dealId]: null }));
    try {
      // 2026-07-22: companion job (user_message) は worker の corrections POST が
      // dedupe_key journal_corr:{id} で自動発行する (吉田商店 #33 取りこぼし恒久修正)。
      // client 側の二重 createJob は廃止 — 応答 r.job で配送結果を確認。
      const r = await createJournalCorrection({ deal_id: dealId, instruction_text: text, created_by: 'web_app_user', learn });
      const jobErr = r?.job?.error;
      if (jobErr) {
        setSendErr((s) => ({
          ...s,
          [dealId]: `指示は保存されましたが Runner への配送 (jobs) に失敗: ${jobErr}`,
        }));
      }
      const now = new Date().toISOString();
      const item: JournalCorrection = {
        id: r.id || Date.now(), company_id: 11518223, deal_id: dealId,
        instruction_text: text, status: 'open', created_by: 'web_app_user',
        created_at: now, updated_at: now,
      };
      setCorrections((cm) => {
        const next = new Map(cm);
        next.set(dealId, [...(next.get(dealId) || []), item]);
        return next;
      });
      setDrafts((d) => ({ ...d, [dealId]: '' }));
    } catch (e: unknown) {
      setSendErr((s) => ({ ...s, [dealId]: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSending((s) => ({ ...s, [dealId]: false }));
    }
  }, [drafts, sending, learnFlags]);

  // 修正指示 解決
  const resolveCorrection = useCallback(async (c: JournalCorrection) => {
    try {
      await updateJournalCorrectionStatus(c.id, 'done');
      setCorrections((cm) => {
        const next = new Map(cm);
        next.set(c.deal_id, (next.get(c.deal_id) || []).map((x) => (x.id === c.id ? { ...x, status: 'done' as const } : x)));
        return next;
      });
    } catch { /* 失敗時は現状維持 */ }
  }, []);

  // レビュー済みマーク トグル (POST /api/journal-review/marks → 成功時のみ表示反映)
  const toggleReviewed = useCallback(async (dealId: number) => {
    if (marking[dealId]) return;
    const next = !marks.get(dealId);
    setMarking((m) => ({ ...m, [dealId]: true }));
    setMarkErr((m) => ({ ...m, [dealId]: null }));
    try {
      await setJournalReviewMark(dealId, next);
      setMarks((prev) => {
        const nm = new Map(prev);
        nm.set(dealId, next);
        return nm;
      });
    } catch (e: unknown) {
      setMarkErr((m) => ({ ...m, [dealId]: `レビュー状態の保存に失敗: ${e instanceof Error ? e.message : String(e)}` }));
    } finally {
      setMarking((m) => ({ ...m, [dealId]: false }));
    }
  }, [marks, marking]);

  // 類似仕訳検索 実行 (GET /api/deals/search — freee読取のみ)
  const runSimilarSearch = useCallback(async (dealId: number) => {
    const j = journals.find((x) => x.id === dealId);
    if (!j || simLoading[dealId]) return;
    const raw = rawDealsRef.current.get(dealId);
    const days = Math.max(0, simDays[dealId] ?? SIM_DEFAULT_DAYS);
    const pct = Math.max(0, simPct[dealId] ?? SIM_DEFAULT_PCT);
    const samePartner = simSamePartner[dealId] !== false;
    const amount = Math.abs(j.amount || j.debitTotal);
    setSimLoading((s) => ({ ...s, [dealId]: true }));
    setSimErr((s) => ({ ...s, [dealId]: null }));
    try {
      const rows = await searchDeals({
        partnerId: samePartner ? (raw?.partner_id ?? null) : null,
        dateFrom: addDaysStr(j.issueDate, -days),
        dateTo: addDaysStr(j.issueDate, days),
        amountMin: amount * (1 - pct / 100),
        amountMax: amount * (1 + pct / 100),
        limit: 50,
      });
      setSimResults((r) => ({ ...r, [dealId]: rows.filter((x) => x.id !== dealId) }));
    } catch (e: unknown) {
      setSimErr((s) => ({ ...s, [dealId]: `検索失敗: ${e instanceof Error ? e.message : String(e)}` }));
    } finally {
      setSimLoading((s) => ({ ...s, [dealId]: false }));
    }
  }, [journals, simDays, simPct, simSamePartner, simLoading]);

  // 類似仕訳検索 パネル開閉 (初回オープン時に初期条件で自動検索)
  const toggleSimilar = useCallback((dealId: number) => {
    const opening = !simOpen.has(dealId);
    setSimOpen((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId); else next.add(dealId);
      return next;
    });
    if (opening && simResults[dealId] === undefined) runSimilarSearch(dealId);
  }, [simOpen, simResults, runSimilarSearch]);

  // 証憑添付パネル 開閉
  const toggleAttach = useCallback((dealId: number) => {
    setAttachOpen((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId); else next.add(dealId);
      return next;
    });
  }, []);

  // 添付成功の共通反映: 証憑数 (worker が数を返せば真値、無ければ +1) + 成功メッセージ
  const applyAttachSuccess = useCallback((dealId: number, r: any, msg: string) => {
    setReceiptCounts((rc) => {
      const cur = rc[dealId] ?? 0;
      const next = typeof r?.receipt_count === 'number' ? r.receipt_count
        : Array.isArray(r?.receipt_ids) ? r.receipt_ids.length
        : cur + 1;
      return { ...rc, [dealId]: next };
    });
    setAttachMsg((s) => ({ ...s, [dealId]: msg }));
  }, []);

  // (1) ファイルボックスNo指定 → POST /api/deals/:id/attach-receipt {receipt_number}
  const attachByNumber = useCallback(async (dealId: number) => {
    if (attachBusy[dealId]) return;
    const rawNo = (fileboxNoDrafts[dealId] || '').trim();
    const no = Number(rawNo);
    if (!rawNo || !Number.isInteger(no) || no <= 0) {
      setAttachErr((s) => ({ ...s, [dealId]: 'ファイルボックスNoは正の整数で入力してください' }));
      return;
    }
    setAttachBusy((s) => ({ ...s, [dealId]: true }));
    setAttachErr((s) => ({ ...s, [dealId]: null }));
    setAttachMsg((s) => ({ ...s, [dealId]: null }));
    try {
      const r = await attachReceiptToDeal(dealId, { receipt_number: no });
      if (r && r.ok === false) throw new Error(r.error || r.message || '添付に失敗しました');
      setFileboxNoDrafts((s) => ({ ...s, [dealId]: '' }));
      applyAttachSuccess(dealId, r, `ファイルボックスNo.${no} を添付しました`);
    } catch (e: unknown) {
      setAttachErr((s) => ({ ...s, [dealId]: `添付失敗: ${e instanceof Error ? e.message : String(e)}` }));
    } finally {
      setAttachBusy((s) => ({ ...s, [dealId]: false }));
    }
  }, [attachBusy, fileboxNoDrafts, applyAttachSuccess]);

  // (2) ファイルアップロード → /api/freee-public/receipts/upload (multipart) → 返却 receipt_id で attach-receipt
  const attachByUpload = useCallback(async (dealId: number, file: File) => {
    if (attachBusy[dealId]) return;
    setAttachBusy((s) => ({ ...s, [dealId]: true }));
    setAttachErr((s) => ({ ...s, [dealId]: null }));
    setAttachMsg((s) => ({ ...s, [dealId]: null }));
    try {
      const up = await uploadFreeeReceipt(file, { description: `#journal_review deal ${dealId} 証憑` });
      const receiptId = up?.receipt_id;
      if (!receiptId) throw new Error('アップロード応答に receipt_id がありません');
      const r = await attachReceiptToDeal(dealId, { receipt_id: receiptId });
      if (r && r.ok === false) throw new Error(r.error || r.message || '添付に失敗しました');
      applyAttachSuccess(dealId, r, `${file.name} をアップロードし添付しました (receipt_id ${receiptId})`);
    } catch (e: unknown) {
      setAttachErr((s) => ({ ...s, [dealId]: `添付失敗: ${e instanceof Error ? e.message : String(e)}` }));
    } finally {
      setAttachBusy((s) => ({ ...s, [dealId]: false }));
    }
  }, [attachBusy, applyAttachSuccess]);

  // 「この構造を指示に引用」→ 修正指示テキストエリアへ自動挿入 + 行を展開してフォームを見せる
  const quoteStructure = useCallback((dealId: number, refDealId: number) => {
    const phrase = `deal_id ${refDealId} と同じ構造で`;
    setDrafts((d) => {
      const cur = d[dealId] || '';
      if (cur.includes(phrase)) return d;
      return { ...d, [dealId]: cur ? `${cur.replace(/\s+$/, '')}\n${phrase}` : phrase };
    });
    ensureExpanded(dealId);
  }, [ensureExpanded]);

  // 部門フィルタ候補 (ロード済み仕訳から)
  const sectionOptions = useMemo(() => {
    const s = new Set<string>();
    for (const j of journals) for (const l of j.lines) {
      if (l.debit?.sectionName) s.add(l.debit.sectionName);
      if (l.credit?.sectionName) s.add(l.credit.sectionName);
    }
    return Array.from(s).sort();
  }, [journals]);

  // 表示リスト (フィルタ+ソート)。既定ソートで未レビューが上 (レビュー済みは常に下へ)
  const visible = useMemo(() => {
    const f = keyField(sortKey);
    const filtered = journals.filter((j) => {
      if (reviewFilter === 'unreviewed' && marks.get(j.id)) return false;
      if (reviewFilter === 'reviewed' && !marks.get(j.id)) return false;
      if (corrFilter) {
        const cs = corrections.get(j.id) || [];
        if (corrFilter === 'open' && !cs.some(isOpenCorrection)) return false;
        // 解決済のみ = done が1件以上あり、かつ未解決(open/in_progress)が残っていない
        if (corrFilter === 'resolved' && !(cs.some((c) => c.status === 'done') && !cs.some(isOpenCorrection))) return false;
      }
      if (sectionFilter && !j.lines.some((l) =>
        l.debit?.sectionName === sectionFilter || l.credit?.sectionName === sectionFilter)) return false;
      if (query) {
        const hay = `${j.title} ${j.summaryText} ${j.refNo} ${j.refNumber || ''} ${j.lines.map((l) => `${l.debit?.accountName || ''} ${l.credit?.accountName || ''}`).join(' ')}`;
        if (!hay.includes(query)) return false;
      }
      return true;
    });
    return [...filtered].sort((a, b) => {
      const ar = marks.get(a.id) ? 1 : 0;
      const br = marks.get(b.id) ? 1 : 0;
      if (ar !== br) return ar - br; // 未レビューが上
      const av = (a[f] as string | null) || '';
      const bv = (b[f] as string | null) || '';
      if (av === '' && bv !== '') return 1;  // null は常に末尾
      if (bv === '' && av !== '') return -1;
      if (av === bv) return b.id - a.id;
      return sortOrder === 'desc' ? (av < bv ? 1 : -1) : (av < bv ? -1 : 1);
    });
  }, [journals, corrections, marks, sortKey, sortOrder, query, sectionFilter, corrFilter, reviewFilter]);

  const debitSum = useMemo(() => visible.reduce((s, j) => s + j.debitTotal, 0), [visible]);
  const unbalancedCount = useMemo(() => visible.filter((j) => !j.balanced).length, [visible]);
  const openTotal = useMemo(() => {
    let n = 0;
    corrections.forEach((arr) => { n += arr.filter(isOpenCorrection).length; });
    return n;
  }, [corrections]);
  // レビュー進捗 (n/N = ロード済み全仕訳に対するレビュー済み件数)
  const reviewedCount = useMemo(() => journals.filter((j) => marks.get(j.id)).length, [journals, marks]);

  const contextBarSt: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: narrow ? '150px 160px 1fr 170px' : '190px 170px 1fr 150px 210px',
    borderBottom: `1px solid ${C.lineDark}`, background: '#f8fafc',
  };

  // 2枠目の日付ブロック: ソートキー連動 (発生日は常時1枠目)
  const secondDate = (j: JournalGroupData): [string, string] => {
    const src = j.tsSource === 'freee' ? 'freee' : '近似';
    if (sortKey === 'created') return [`登録日(${src})`, fmtStamp(j.createdAt)];
    if (sortKey === 'updated') return [`更新日(${src})`, fmtStamp(j.updatedAt)];
    return ['支払期日', fmtDate(j.dueDate)];
  };

  // 類似仕訳検索 結果行の名称解決 (worker が名称を返せば優先、無ければ masters Map 引き)
  const simRowNames = (r: DealSearchRow): { accounts: string; sections: string; desc: string } => {
    const accs: string[] = []; const secs: string[] = []; const descs: string[] = [];
    for (const dt of r.details || []) {
      const an = dt.account_item_name
        || (dt.account_item_id != null ? (maps?.accounts.get(Number(dt.account_item_id)) || `勘定#${dt.account_item_id}`) : '');
      if (an && !accs.includes(an)) accs.push(an);
      const sn = dt.section_name
        || (dt.section_id != null ? (maps?.sections.get(Number(dt.section_id)) || `部門#${dt.section_id}`) : '');
      if (sn && !secs.includes(sn)) secs.push(sn);
      if (dt.description) descs.push(dt.description);
    }
    if (!descs.length && r.description) descs.push(r.description);
    return {
      accounts: accs.slice(0, 3).join('・') + (accs.length > 3 ? ` 他${accs.length - 3}` : ''),
      sections: secs.slice(0, 3).join('・') + (secs.length > 3 ? ` 他${secs.length - 3}` : ''),
      desc: descs.join(' / '),
    };
  };

  return (
    <div style={S.page}>
      <style>{PAGE_CSS}</style>
      <Container>

        {mapsError && <div style={{ marginTop: 12 }}><MessageBlock error>{mapsError}</MessageBlock></div>}
        {error && <div style={{ marginTop: 12 }}><MessageBlock error>{error}</MessageBlock></div>}

        <section style={S.sheet}>
          {/* 処理中オーバーレイ: freee同期/取得中はページ内の全操作を遮断 (スクロールは可) */}
          {(syncing || loading) && (
            <div style={S.overlay} aria-busy="true">
              <div style={S.overlayCard}>
                <InlineSpinner isLoading />
                <span>
                  {syncing ? (syncMsg || 'freee 仕訳帳と同期中です…(この間ページは操作できません)') : 'freee から取引を取得中です…'}
                </span>
              </div>
            </div>
          )}
          {/* navy ヘッダ */}
          <header style={S.top}>
            <div style={{ ...S.topTitleWrap, alignItems: 'center', flexWrap: 'wrap' }}>
              <h1 style={S.topTitle}>複式仕訳プレビュー</h1>
              <span style={S.topCaption}>登録済み仕訳のレビューと修正。修正指示を書くとAIがfreeeの仕訳を直接修正します</span>
              <span style={{ ...S.readonlyPill, flex: '0 0 auto' }} title="このページ自体は freee へ書込みません。修正指示はミニPCのAI担当が受け取り、freee の仕訳を直接更新します">
                <MdSmartToy size={13} /> 修正反映はAI担当が実施
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...S.headerChip, ...(journals.length > 0 && reviewedCount >= journals.length ? { borderColor: '#7fd0b6', color: '#bdeedd' } : {}) }}
                title="ロード済みの仕訳のうちレビュー済みにした件数">
                レビュー済み {reviewedCount}/{journals.length}
              </span>
              <span style={S.headerChip}>表示形式: 仕訳ごと</span>
            </div>
          </header>

          {/* context bar */}
          <section style={contextBarSt}>
            <div style={S.contextItem}>
              <span style={S.contextLabel}>対象期間</span>
              <div style={{ ...S.contextValue, fontFamily: NUM, fontSize: narrow ? 12 : 13 }}>{loadedRange || '未取得'}</div>
            </div>
            <div style={S.contextItem}>
              <span style={S.contextLabel}>会計単位</span>
              <div style={S.contextValue}>{sectionFilter || 'クマガイ / 全部門'}</div>
            </div>
            <div style={S.contextItem}>
              <span style={S.contextLabel}>表示対象</span>
              <div style={S.contextValue}>登録済(決済済・未決済)の取引</div>
              <div style={S.contextNote}>
                借貸不一致 {unbalancedCount}件は警告表示 ／ 修正待ち {openTotal}件 (AI担当がfreeeへ反映後「修正完了」) ／ レビュー済み {reviewedCount}/{journals.length}
              </div>
            </div>
            <div style={S.contextItem}>
              <span style={S.contextLabel}>仕訳件数</span>
              <div style={{ ...S.contextValue, fontFamily: NUM, fontVariantNumeric: 'tabular-nums' }}>{visible.length}件</div>
            </div>
            {!narrow && (
              <div style={{ ...S.contextItem, borderRight: 0 }} className="jr-context-last">
                <span style={S.contextLabel}>表示中の借方合計</span>
                <div style={{ ...S.contextValue, fontFamily: NUM, fontVariantNumeric: 'tabular-nums' }}>{fmtYen(debitSum)}</div>
              </div>
            )}
          </section>

          {/* toolbar: ソート・展開 */}
          <section style={S.toolbar}>
            <div style={S.toolbarLead}>
              <h2 style={S.toolbarH2}>仕訳一覧</h2>
              <p style={S.toolbarP}>日付で並べ替え、展開して明細確認・修正指示・レビュー済み管理 (未レビューが上に並びます)</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={S.sortControl}>
                <span style={S.sortLabel}>並び順</span>
                {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                  <button key={k} type="button" className="jr-hoverbtn" style={sortKeyBtnSt(sortKey === k)}
                    onClick={() => setSortKey(k)} aria-pressed={sortKey === k} title={SORT_TITLES[k]}>
                    {SORT_LABELS[k]}
                  </button>
                ))}
                <button type="button" className="jr-hoverbtn" style={sortOrderBtnSt}
                  onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}>
                  {sortOrder === 'desc' ? '新しい順 ↓' : '古い順 ↑'}
                </button>
              </div>
              <button type="button" className="jr-hoverbtn" style={viewBtnSt} onClick={expandAll}>すべて展開</button>
              <button type="button" className="jr-hoverbtn" style={viewBtnSt} onClick={collapseAll}>すべて閉じる</button>
            </div>
            {(sortKey === 'created' || sortKey === 'updated') && (
              <p style={S.toolbarHint}>
                <MdInfoOutline size={13} style={{ flex: '0 0 auto', color: '#8092a5' }} />
                登録日/更新日は freee 仕訳帳の作成日時・更新日時(真値・分単位)です。未同期の取引のみ FREEEMAN 把握時刻で近似表示します(「freee同期」で真値に更新)
              </p>
            )}
          </section>

          {/* filter row */}
          <section style={S.filterRow}>
            <span style={S.filterLabel}>期間</span>
            <input type="date" style={S.filterInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span style={{ color: C.sub }}>～</span>
            <input type="date" style={S.filterInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <select style={S.filterInput} value={dateField} onChange={(e) => setDateField(e.target.value as JournalReviewDateField)}>
              <option value="issue">発生日で絞る</option>
              <option value="due">支払期日で絞る</option>
              <option value="created">登録日(freee)で絞る</option>
              <option value="updated">更新日(freee)で絞る</option>
            </select>
            <button type="button" className="jr-hoverbtn" style={{ ...viewBtnSt, height: 30 }}
              disabled={syncing} onClick={runFreeeSync}
              title="freee 側で取引を登録・修正した後に押すと、登録日/更新日(真値)が最新になります。仕訳帳(直近12ヶ月)を読取のみで同期・数十秒かかります">
              {syncing ? '同期中…' : 'freee同期'}
            </button>
            {syncMsg && <span style={S.filterNote}>{syncMsg}</span>}
            {(dateField === 'created' || dateField === 'updated') && !syncMsg && (
              <span style={S.filterNote}>
                <MdInfoOutline size={13} style={{ color: '#8092a5' }} />
                freee 仕訳帳の作成/更新日時(真値・分単位)で絞り込みます
              </span>
            )}
            <Button appearance="primary" small onClick={load} disabled={loading || !maps}>
              {loading ? <InlineSpinner isLoading /> : <MdRefresh />} 取得
            </Button>
            <span style={{ width: 8 }} />
            <select style={S.filterInput} value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
              <option value="">全部門</option>
              {sectionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="text" style={{ ...S.filterInput, width: 210 }} placeholder="検索 (取引先・科目・ref)"
              value={query} onChange={(e) => setQuery(e.target.value)} />
            <select style={S.filterInput} value={corrFilter}
              onChange={(e) => setCorrFilter(e.target.value as '' | 'open' | 'resolved')}
              title="解決済のみ = 解決済の指示があり、対応中の指示が残っていない仕訳">
              <option value="">修正指示: 全て表示</option>
              <option value="open">対応中の修正指示のみ</option>
              <option value="resolved">解決済の修正指示のみ</option>
            </select>
            <select style={S.filterInput} value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value as 'all' | 'unreviewed' | 'reviewed')}
              title="レビュー済み状態で絞り込み (既定ソートでは未レビューが上に並びます)">
              <option value="all">レビュー: 全て</option>
              <option value="unreviewed">未レビューのみ</option>
              <option value="reviewed">レビュー済みのみ</option>
            </select>
          </section>

          {/* 仕訳一覧 */}
          <section style={S.list}>
            {!maps && !mapsError && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub }}><InlineSpinner isLoading /> マスタ読込中…</div>
            )}
            {maps && loading && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub }}><InlineSpinner isLoading /> freee から取引を取得中…</div>
            )}
            {maps && !loading && visible.length === 0 && (
              <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontWeight: 700 }}>対象期間に仕訳がありません</div>
            )}

            {!loading && visible.map((j) => {
              const isOpen = expanded.has(j.id);
              const corrs = corrections.get(j.id) || [];
              const openCount = corrs.filter(isOpenCorrection).length;
              const doneCount = corrs.filter((c) => c.status === 'done').length;
              const reviewed = !!marks.get(j.id);
              const simIsOpen = simOpen.has(j.id);
              const attachIsOpen = attachOpen.has(j.id);
              const receiptN = receiptCounts[j.id] ?? 0;
              const [d2label, d2value] = secondDate(j);
              const diff = j.debitTotal - j.creditTotal;
              return (
                <div key={j.id} style={groupSt(openCount > 0, reviewed)}>
                  {/* summary */}
                  <div className="jr-summary" style={summarySt(isOpen, narrow, reviewed)} role="button" tabIndex={0}
                    onClick={() => toggleExpand(j.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(j.id); } }}>
                    <div style={{ ...S.groupNo, ...(openCount > 0 ? { background: C.warnBg } : {}) }}>
                      <small style={S.groupNoSmall}>仕訳</small>
                      {String(j.id).slice(-6)}
                      <a href={`https://secure.freee.co.jp/deals#deal_id=${j.id}`} target="_blank" rel="noopener noreferrer"
                        className="jr-hoverbtn" style={S.freeeJump} title="freee でこの取引を開く"
                        onClick={(e) => e.stopPropagation()}>
                        freee ↗
                      </a>
                    </div>
                    <div style={S.groupDates}>
                      <div style={S.dateBlock}>
                        <span style={S.dateLabel}>発生日</span>
                        <b style={S.dateValue}>{fmtDate(j.issueDate)}</b>
                      </div>
                      <div style={S.dateBlock2}>
                        <span style={S.dateLabel}>{d2label}</span>
                        <b style={S.dateValue}>{d2value}</b>
                      </div>
                    </div>
                    <div style={S.groupMain}>
                      <div style={S.groupMainTop}>
                        <h3 style={S.groupH3}>{j.title}</h3>
                        <span style={S.journalId}>{j.refNo}</span>
                        <span style={S.settledBadge(j.settled)}>{j.settled ? '決済済' : '未決済'}</span>
                        {reviewed && <span style={S.reviewedBadge}><MdCheckCircle size={12} /> レビュー済み ✓</span>}
                        {receiptN > 0 && (
                          <span style={S.reviewedBadge} title={`この取引に添付済みの証憑 ${receiptN}件`}>
                            <MdAttachFile size={12} /> 証憑✓ {receiptN}件
                          </span>
                        )}
                        {/* 修正指示 状態チップ: 修正待ち(橙 open/in_progress) / 修正完了(緑 done)。クリックで指示内容と経過を展開 */}
                        {openCount > 0 && (
                          <span style={S.corrChipWait} role="button"
                            title="修正待ち (AI担当が対応中)。クリックで指示内容と経過を表示"
                            onClick={(e) => { e.stopPropagation(); ensureExpanded(j.id); }}>
                            <MdEditNote size={12} /> 修正待ち {openCount}
                          </span>
                        )}
                        {openCount === 0 && doneCount > 0 && (
                          <span style={S.corrChipDone} role="button"
                            title="修正完了 (AI担当が freee へ反映済み)。クリックで指示内容と経過を表示"
                            onClick={(e) => { e.stopPropagation(); ensureExpanded(j.id); }}>
                            <MdCheckCircle size={12} /> 修正完了 {doneCount > 1 ? doneCount : ''}
                          </span>
                        )}
                        {!j.balanced && <span style={{ ...S.openBadge, color: C.danger, borderColor: '#e6b0ac', background: C.dangerBg }}><MdWarningAmber size={12} /> 借貸不一致</span>}
                        <button type="button" className="jr-hoverbtn"
                          style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11, ...(reviewed ? {} : { borderColor: '#9fdbc7', color: C.good }) }}
                          disabled={!!marking[j.id]}
                          title={reviewed ? 'レビュー済みを解除して未レビューに戻す' : 'この仕訳をレビュー済みにする (行がグレーアウトし一覧の下へ)'}
                          onClick={(e) => { e.stopPropagation(); toggleReviewed(j.id); }}>
                          {marking[j.id] ? '保存中…' : reviewed ? '未レビューに戻す' : 'レビュー済みにする'}
                        </button>
                        <button type="button" className="jr-hoverbtn"
                          style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11, ...(simIsOpen ? { background: C.blueTint, color: '#234e79' } : {}) }}
                          title="同一取引先・発生日±90日・金額±20% を初期条件に類似仕訳を検索 (条件は変更可)"
                          onClick={(e) => { e.stopPropagation(); toggleSimilar(j.id); }}>
                          <MdSearch size={12} style={{ verticalAlign: '-2px' }} /> 類似仕訳を検索
                        </button>
                        <button type="button" className="jr-hoverbtn"
                          style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11, ...(attachIsOpen ? { background: C.blueTint, color: '#234e79' } : {}) }}
                          title="ファイルボックスNo指定 または ファイルアップロードで、この freee 取引に証憑を添付"
                          onClick={(e) => { e.stopPropagation(); toggleAttach(j.id); }}>
                          <MdAttachFile size={12} style={{ verticalAlign: '-2px' }} /> 証憑を添付
                        </button>
                        {markErr[j.id] && <span style={{ color: C.danger, fontSize: 10.5, fontWeight: 700 }}>{markErr[j.id]}</span>}
                      </div>
                      <p style={S.groupP}>{j.summaryText}</p>
                    </div>
                    <div style={S.groupTotal}>
                      <div style={S.groupTotalMeta}>
                        <span style={S.groupTotalLabel}>借方合計 = 貸方合計</span>
                        <b style={{ color: j.balanced ? C.good : C.danger, fontSize: 11, fontWeight: 800 }}>
                          {j.balanced ? '借貸一致' : `差額 ${fmtYen(Math.abs(diff))}`}
                        </b>
                      </div>
                      <div style={S.groupTotalValue}>{fmtYen(j.debitTotal)}</div>
                    </div>
                    <div className={`jr-chevron${isOpen ? ' open' : ''}`} style={S.chevron}>
                      <MdExpandMore size={16} />
                    </div>
                  </div>

                  {/* 証憑を添付 — 行下インライン展開 (2026-07-13)。書込は worker (UIクリック起点=R2準拠) */}
                  {attachIsOpen && (
                    <div style={S.simPanel}>
                      <div style={S.simHead}>
                        <MdAttachFile size={14} /> 証憑を添付
                        <span style={{ color: C.sub, fontWeight: 600 }}>
                          ファイルボックスNo指定 または ファイルアップロードで freee 取引 #{j.id} に添付します
                        </span>
                        <button type="button" className="jr-hoverbtn"
                          style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11, marginLeft: 'auto' }}
                          onClick={() => toggleAttach(j.id)}>
                          閉じる
                        </button>
                      </div>
                      <div style={S.simConds}>
                        <span>ファイルボックスNo</span>
                        <input type="number" min={1} style={{ ...S.simNumInput, width: 110 }} placeholder="例: 123456"
                          value={fileboxNoDrafts[j.id] || ''} disabled={!!attachBusy[j.id]}
                          onChange={(e) => setFileboxNoDrafts((s) => ({ ...s, [j.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); attachByNumber(j.id); } }} />
                        <button type="button" className="jr-hoverbtn" style={{ ...viewBtnSt, height: 26, fontSize: 11 }}
                          disabled={!!attachBusy[j.id] || !(fileboxNoDrafts[j.id] || '').trim()}
                          title="freee ファイルボックスの証憑Noを指定して、この取引に添付"
                          onClick={() => attachByNumber(j.id)}>
                          この番号を添付
                        </button>
                        <span style={{ color: C.sub, fontWeight: 600 }}>／</span>
                        <span>ファイルをアップロード</span>
                        <input type="file" accept="application/pdf,image/*" disabled={!!attachBusy[j.id]}
                          style={{ font: `600 11px ${FONT}`, maxWidth: 260 }}
                          title="PDF/画像をファイルボックスへアップロードし、この取引に添付"
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            if (f) attachByUpload(j.id, f);
                            e.target.value = '';
                          }} />
                        {attachBusy[j.id] && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: C.sub, fontWeight: 700 }}>
                            <InlineSpinner isLoading /> 添付中…
                          </span>
                        )}
                      </div>
                      {attachErr[j.id] && (
                        <div style={{ padding: '8px 13px', color: C.danger, fontSize: 11, fontWeight: 700 }}>{attachErr[j.id]}</div>
                      )}
                      {!attachErr[j.id] && attachMsg[j.id] && (
                        <div style={{ padding: '8px 13px', color: C.good, fontSize: 11, fontWeight: 700 }}>
                          <MdCheckCircle size={12} style={{ verticalAlign: '-2px' }} /> {attachMsg[j.id]} — 証憑✓ {receiptN}件
                        </div>
                      )}
                    </div>
                  )}

                  {/* 類似仕訳検索 — 行下インライン展開 (freee 読取のみ) */}
                  {simIsOpen && (
                    <div style={S.simPanel}>
                      <div style={S.simHead}>
                        <MdSearch size={14} /> 類似仕訳検索
                        <span style={{ color: C.sub, fontWeight: 600 }}>
                          初期条件: 同一取引先・発生日±{SIM_DEFAULT_DAYS}日・金額±{SIM_DEFAULT_PCT}% (条件は変更可・freee読取のみ)
                        </span>
                        <button type="button" className="jr-hoverbtn"
                          style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11, marginLeft: 'auto' }}
                          onClick={() => toggleSimilar(j.id)}>
                          閉じる
                        </button>
                      </div>
                      <div style={S.simConds}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                          <input type="checkbox" checked={simSamePartner[j.id] !== false}
                            onChange={(e) => setSimSamePartner((s) => ({ ...s, [j.id]: e.target.checked }))} />
                          同一取引先{rawDealsRef.current.get(j.id)?.partner_id != null ? ` (${j.title})` : ' (この取引に取引先なし)'}
                        </label>
                        <span>発生日 ±</span>
                        <input type="number" min={0} style={S.simNumInput} value={simDays[j.id] ?? SIM_DEFAULT_DAYS}
                          onChange={(e) => setSimDays((s) => ({ ...s, [j.id]: Number(e.target.value) }))} />
                        <span>日</span>
                        <span style={{ marginLeft: 6 }}>金額 ±</span>
                        <input type="number" min={0} style={S.simNumInput} value={simPct[j.id] ?? SIM_DEFAULT_PCT}
                          onChange={(e) => setSimPct((s) => ({ ...s, [j.id]: Number(e.target.value) }))} />
                        <span>%</span>
                        <button type="button" className="jr-hoverbtn" style={{ ...viewBtnSt, height: 26, fontSize: 11 }}
                          disabled={!!simLoading[j.id]} onClick={() => runSimilarSearch(j.id)}>
                          {simLoading[j.id] ? '検索中…' : 'この条件で再検索'}
                        </button>
                      </div>
                      {simErr[j.id] && (
                        <div style={{ padding: '8px 13px', color: C.danger, fontSize: 11, fontWeight: 700 }}>{simErr[j.id]}</div>
                      )}
                      {simLoading[j.id] && (
                        <div style={{ padding: '10px 13px', color: C.sub, fontSize: 12 }}><InlineSpinner isLoading /> 類似仕訳を検索中…</div>
                      )}
                      {!simLoading[j.id] && !simErr[j.id] && simResults[j.id] != null && (simResults[j.id] || []).length === 0 && (
                        <div style={{ padding: '10px 13px', color: C.sub, fontSize: 12, fontWeight: 700 }}>条件に合う類似仕訳は見つかりませんでした</div>
                      )}
                      {!simLoading[j.id] && (simResults[j.id] || []).length > 0 && (
                        <div style={S.simTableWrap}>
                          <table style={S.simTable}>
                            <thead>
                              <tr>
                                <th style={S.simTh}>deal_id</th>
                                <th style={S.simTh}>日付</th>
                                <th style={{ ...S.simTh, textAlign: 'right' }}>金額</th>
                                <th style={S.simTh}>勘定</th>
                                <th style={S.simTh}>部門</th>
                                <th style={S.simTh}>摘要</th>
                                <th style={S.simTh} aria-label="操作" />
                              </tr>
                            </thead>
                            <tbody>
                              {(simResults[j.id] || []).map((r) => {
                                const nm = simRowNames(r);
                                return (
                                  <tr key={r.id}>
                                    <td style={{ ...S.simTd, fontFamily: NUM, fontSize: 11, whiteSpace: 'nowrap' }}>
                                      <a href={`https://secure.freee.co.jp/deals#deal_id=${r.id}`} target="_blank" rel="noopener noreferrer"
                                        style={{ color: '#476b8e' }} title="freee でこの取引を開く">
                                        {r.id}
                                      </a>
                                    </td>
                                    <td style={{ ...S.simTd, fontFamily: NUM, whiteSpace: 'nowrap' }}>{fmtDate(r.issue_date)}</td>
                                    <td style={{ ...S.simTd, fontFamily: NUM, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                      {r.amount != null ? fmtYen(Number(r.amount)) : '—'}
                                    </td>
                                    <td style={S.simTd}>{nm.accounts || '—'}</td>
                                    <td style={S.simTd}>{nm.sections || '—'}</td>
                                    <td style={{ ...S.simTd, color: C.sub }}>{nm.desc || '—'}</td>
                                    <td style={{ ...S.simTd, whiteSpace: 'nowrap' }}>
                                      <button type="button" className="jr-hoverbtn"
                                        style={{ ...viewBtnSt, height: 24, padding: '0 8px', fontSize: 11 }}
                                        title={`修正指示テキストエリアに『deal_id ${r.id} と同じ構造で』を挿入`}
                                        onClick={() => quoteStructure(j.id, r.id)}>
                                        この構造を指示に引用
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* detail */}
                  {isOpen && (
                    <div style={S.detailArea}>
                      <div style={S.detailBar}>
                        <div style={S.detailBarLeft}>
                          <h4 style={S.detailH4}>仕訳明細</h4>
                          <p style={S.detailP}>
                            取引先・部門・タグは各勘定科目の直下に配置
                            {!j.detailFetched && ' ／ 摘要を取得中…'}
                          </p>
                        </div>
                        <div className={`jr-detail-status${j.balanced ? '' : ' ng'}`}>
                          {j.balanced ? '借方・貸方の合計が一致しています' : `借方と貸方に ${fmtYen(Math.abs(diff))} の差額があります`}
                        </div>
                      </div>

                      <div style={S.ledger} role="table" aria-label={`${j.refNo} 複式仕訳明細`}>
                        <div style={{ ...ledgerGridSt(narrow), ...S.ledgerHead }} role="row">
                          <div style={S.lineHead}>行</div>
                          <div style={sideHeadSt('debit')}>
                            <div style={S.sideTitle}>
                              <span style={sideStampSt('debit')}>借方</span>
                              <strong style={{ ...S.sideStrong, color: '#33699e' }}>借方明細</strong>
                              <small className="jr-side-sub" style={S.sideSmallHead}>費用・資産の増加</small>
                            </div>
                            <small className="jr-side-sub" style={S.sideSmallHead}>科目 / 管理属性 / 摘要</small>
                          </div>
                          <div style={S.amountHead}>金額（税込）</div>
                          <div className="jr-bridge-head" />
                          <div style={sideHeadSt('credit')}>
                            <div style={S.sideTitle}>
                              <span style={sideStampSt('credit')}>貸方</span>
                              <strong style={{ ...S.sideStrong, color: '#a46922' }}>貸方明細</strong>
                              <small className="jr-side-sub" style={S.sideSmallHead}>資産の減少・負債の増加</small>
                            </div>
                            <small className="jr-side-sub" style={S.sideSmallHead}>科目 / 管理属性 / 摘要</small>
                          </div>
                          <div style={{ ...S.amountHead, borderRight: 0 }}>金額（税込）</div>
                        </div>

                        {j.lines.map((l: LedgerLineData, i: number) => (
                          <div key={l.no} style={{ ...ledgerGridSt(narrow), ...ledgerRowSt(i % 2 === 1), ...(i === j.lines.length - 1 ? { borderBottom: 0 } : {}) }} role="row">
                            <div style={S.lineNo}>{String(l.no).padStart(2, '0')}</div>
                            <EntryCell cell={l.debit} side="debit" />
                            <AmountCell cell={l.debit} />
                            <div className="jr-bridge" />
                            <EntryCell cell={l.credit} side="credit" />
                            {l.credit
                              ? <div style={{ ...S.amountCell, borderRight: 0 }}><div style={S.amount}>{l.credit.amount.toLocaleString('ja-JP')}</div><div style={S.taxCode}>{l.credit.kind === 'detail' ? (l.credit.taxLabel || '') : l.credit.kind === 'payment' ? '決済' : '未決済'}</div></div>
                              : <div style={{ ...S.amountBlank, borderRight: 0 }}>—</div>}
                          </div>
                        ))}
                      </div>

                      <div style={S.groupFoot}>
                        <div style={S.groupNote}>
                          <b style={{ color: '#42576e', fontWeight: 800 }}>備考:</b>{' '}
                          {j.type === 'income' ? '収入取引' : '支出取引'}
                          {j.refNumber ? ` ／ 管理番号 ${j.refNumber}` : ''}
                          {` ／ 登録日時 ${fmtStamp(j.createdAt)} ／ 更新日時 ${fmtStamp(j.updatedAt)} (${j.tsSource === 'freee' ? 'freee真値' : 'FREEEMAN近似'})`}
                        </div>
                        <div style={groupBalanceSt(j.balanced)}>
                          <span style={{ color: j.balanced ? C.good : C.danger, fontSize: 11, fontWeight: 800 }}>差額</span>
                          <b style={{ color: j.balanced ? '#1f6550' : C.danger, fontFamily: NUM, fontSize: 15, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
                            {fmtYen(Math.abs(diff))}
                          </b>
                        </div>
                      </div>

                      {/* 修正指示 */}
                      <div style={S.correctionPanel}>
                        <div style={S.correctionHead}>
                          <MdEditNote size={15} /> この仕訳への修正指示
                          <span style={{ color: C.sub, fontWeight: 600 }}>
                            (修正指示を書くとAI担当がfreeeの仕訳を直接修正します。反映が済むと「修正完了」になります)
                          </span>
                        </div>
                        {corrs.map((c) => {
                          const co = isOpenCorrection(c);
                          return (
                            <div key={c.id} style={correctionItemSt(co)}>
                              <span style={correctionStatusSt(co)} title={c.status}>
                                {co ? '修正待ち' : c.status === 'done' ? '修正完了' : correctionStatusToUnified(c.status).label}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.instruction_text}</div>
                                <div style={{ marginTop: 3, color: C.sub, fontSize: 10, fontFamily: NUM }}>
                                  指示 {fmtStamp(c.created_at)}
                                  {co && ' — AI担当が対応中'}
                                  {c.status === 'done' && ` → 完了 ${fmtStamp(c.updated_at)}`}
                                </div>
                              </div>
                              {co && (
                                <button type="button" className="jr-hoverbtn"
                                  style={{ ...viewBtnSt, height: 26, fontSize: 11, flex: '0 0 auto' }}
                                  onClick={() => resolveCorrection(c)}>
                                  解決にする
                                </button>
                              )}
                            </div>
                          );
                        })}
                        <div style={S.correctionForm}>
                          <textarea
                            style={S.correctionTextarea}
                            maxLength={2000}
                            placeholder="例: 部門を渋谷店に変更 ／ 勘定科目を消耗品費→修繕費 (Ctrl+Enter で送信)"
                            value={drafts[j.id] || ''}
                            onChange={(e) => setDrafts((d) => ({ ...d, [j.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submitCorrection(j.id); }
                            }}
                          />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
                            <label style={S.learnLabel} title="ON: この指示を今後の同種仕訳のルールとして学習 / OFF: この1件だけに適用">
                              <input type="checkbox" checked={learnFlags[j.id] !== false}
                                onChange={(e) => setLearnFlags((f) => ({ ...f, [j.id]: e.target.checked }))} />
                              この指示を今後のルールとして学習する
                            </label>
                            <Button appearance="primary" small
                              disabled={!(drafts[j.id] || '').trim() || !!sending[j.id]}
                              onClick={() => submitCorrection(j.id)}>
                              {sending[j.id] ? <InlineSpinner isLoading /> : '指示を送信'}
                            </Button>
                            {learnFlags[j.id] === false && (
                              <span style={S.learnOffNote}>一回限りの適用(学習しない)</span>
                            )}
                            {sendErr[j.id] && <span style={{ color: C.danger, fontSize: 11 }}>{sendErr[j.id]}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </section>
      </Container>
    </div>
  );
};

export default JournalReviewPage;
